import * as React from 'react';
import { useDispatch, useIntl } from 'umi';
import { Button, Space, Tag } from 'antd';
import {
  BlockOutlined,
  ClockCircleOutlined,
  DownOutlined,
  EnvironmentOutlined,
  HistoryOutlined,
  SyncOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { ServiceRequestModel, ServiceRequestStatus } from '@/typings/models/ServiceRequest';
import { LeadModel } from '@/typings/models/Lead';

import Card from '@/components/Card';
import { F } from '@/utils/intl';

import styles from './index.less';
import ServiceRequestStatusTag from '@/components/Lead/ServiceRequestStatusTag';
import { FormatDate } from '@/components/PrettyDate';
import ServiceRequestMigrationInfo from '@/components/Lead/ServiceRequestMigrationInfo';
import classNames from 'classnames';
import ServiceRequestAction from '@/components/Lead/ServiceRequestAction';
import { BranchActionTag, LeadActionTag } from '@/components/ActionTag';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import ServiceRequestAssignee from '@/components/Lead/ServiceRequestAssignee';
import LeadAssigneeOverview from '@/components/Lead/LeadAssigneeOverview';
import { getServiceRequestAssignee } from '@/components/Kanban/ServiceRequests/Calendar/utils';
import VideoLinkButton from '@/components/VideoLinkButton';
import { describeRecurring, isRecurringEvent } from '@/utils/recurrence';

export interface ServiceRequestItemProps {
  serviceRequest: ServiceRequestModel;
  lead: LeadModel;
  cardClassName?: string;
  onRefresh: () => void;
  ghostActions?: boolean;
  bordered?: boolean;
  showLead?: boolean;
  hideActions?: boolean;
  hideExtraFields?: boolean;
  hideMigrationSource?: boolean;
  onItemClick?: () => void;
  listView?: boolean;
}

export function ServiceRequestItem({
  serviceRequest,
  lead,
  cardClassName,
  onRefresh,
  ghostActions = false,
  bordered = true,
  showLead = false,
  hideActions = false,
  hideExtraFields = false,
  hideMigrationSource = false,
  onItemClick,
  listView = false,
}: ServiceRequestItemProps) {
  const dispatch = useDispatch();
  const intl = useIntl();

  // The lead/project payloads may only carry recurring_id (no pattern data) —
  // fall back to a generic label instead of the full rule description then.
  const recurringLabel = isRecurringEvent(serviceRequest)
    ? (serviceRequest.recurring
      ? describeRecurring(serviceRequest.recurring, intl)
      : intl.formatMessage({ id: 'pages.calendar.recurrence.recurring', defaultMessage: 'Recurring' }))
    : null;

  const openDetailsDrawer = () => {
    if (serviceRequest.blocker)
      return dispatch({
        type: 'serviceRequest/toggleCalendarEventDetailsDrawer',
        payload: { calendarEventId: serviceRequest.id },
      });
    dispatch({
      type: 'serviceRequest/toggleDetailsDrawer',
      payload: { appointmentId: serviceRequest.id },
    });
  };
  const [isInactiveEventOpen, setIsInactiveEventOpen] = React.useState(false);
  const [isExtraFieldsExpanded, setIsExtraFieldsExpanded] =
    React.useState(false);

  const isGrayedOut = (serviceRequest: ServiceRequestModel) =>
    (serviceRequest.service || serviceRequest.what) &&
    ![ServiceRequestStatus.confirmed, ServiceRequestStatus.tentative].includes(
      serviceRequest.status,
    );

  const isCustomAppointment = (serviceRequest: ServiceRequestModel) =>
    !serviceRequest.service || !serviceRequest.what;

  const onActionEnd = () => {
    dispatch({
      type: 'serviceRequest/updateFetchCalendarViewAt',
    });
    onRefresh();
  };

  const agent = getServiceRequestAssignee(serviceRequest);

  if (listView) {
    const titleText = serviceRequest.title || serviceRequest.branch_service?.service_type?.name || serviceRequest.what;
    const serviceTypeName = serviceRequest.branch_service?.service_type?.name;

    return (
      <div
        className={classNames(styles.appointmentListItem, {
          [styles.blockerListItem]: serviceRequest.blocker,
          [styles.clickable]: !!onItemClick,
          [styles.grayedOutServiceRequest]: isGrayedOut(serviceRequest),
        })}
        style={serviceRequest.blocker && serviceRequest.color ? { '--event-color': serviceRequest.color } : undefined}
        onClick={onItemClick ? openDetailsDrawer : undefined}
      >
        <div className={styles.appointmentListTimeCol}>
          <span className={styles.appointmentListDate}>
            <FormatDate format="ddd, D. MMM" date={serviceRequest.utc_start_time} />
          </span>
          <span className={styles.appointmentListTime}>
            <FormatDate date={serviceRequest.utc_start_time} onlyTime />
            {serviceRequest.utc_end_time && (
              <> – <FormatDate date={serviceRequest.utc_end_time} onlyTime /></>
            )}
          </span>
        </div>
        <div className={styles.appointmentListContent}>
          <div className={styles.appointmentListTitle}>
            {titleText}
            {serviceRequest.status && <ServiceRequestStatusTag fontSize={11} status={serviceRequest.status} />}
          </div>
          <div className={styles.appointmentListMeta}>
            {serviceRequest.service && serviceRequest.branch_service?.branch && (
              <div className={styles.appointmentListMetaRow} onClick={(e) => onItemClick && e.stopPropagation()}>
                <EnvironmentOutlined />
                <BranchActionTag showAddressTooltip size="small" branch={serviceRequest.branch_service.branch} />
              </div>
            )}
            {serviceTypeName && (
              <div className={styles.appointmentListMetaRow}>
                <TagOutlined />
                <span>{serviceTypeName}</span>
              </div>
            )}
            {recurringLabel && (
              <div className={styles.appointmentListMetaRow}>
                <SyncOutlined />
                <span>{recurringLabel}</span>
              </div>
            )}
            {serviceRequest.created && (
              <div className={styles.appointmentListMetaRow}>
                <HistoryOutlined />
                <span>
                  <F
                    id="pages.leads.serviceRequests.createdOn"
                    defaultMessage="Created {date}"
                    values={{ date: <FormatDate format="D. MMM YYYY" date={serviceRequest.created} /> }}
                  />
                </span>
              </div>
            )}
            {agent && (
              <div className={styles.appointmentListMetaRow} onClick={(e) => onItemClick && e.stopPropagation()}>
                <UserOutlined />
                <LeadAssigneeOverview serviceRequest={serviceRequest} />
              </div>
            )}
          </div>
          <div className={styles.appointmentListActions} onClick={(e) => e.stopPropagation()}>
            <VideoLinkButton videoCall={serviceRequest.video_call} size="small" shape="circle" />
            <ServiceRequestAction
              serviceRequest={serviceRequest}
              lead={lead}
              onActionEnd={onActionEnd}
              ghostActions={ghostActions}
              hideActions={hideActions}
            />
          </div>
        </div>
      </div>
    );
  }

  if (isGrayedOut(serviceRequest) && !isInactiveEventOpen)
    return (
      <Card
        className={classNames(styles.appointmentCard, styles.miniAppointment, onItemClick && styles.clickable)}
        onClick={onItemClick ? openDetailsDrawer : () => setIsInactiveEventOpen(true)}
        cardContentClassName={classNames({
          [styles.grayedOutServiceRequest]: isGrayedOut(serviceRequest),
          [styles.blockerAppointment]: !serviceRequest.service,
        })}
      >
        <Space>
          <Button
            type={'link'}
            icon={<DownOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setIsInactiveEventOpen(true);
            }}
          />
          <small>{serviceRequest.title || serviceRequest.what}</small>
          {serviceRequest.status && (
            <ServiceRequestStatusTag
              fontSize={10}
              status={serviceRequest.status}
            />
          )}
          <small>
            <Space size={4}>
              <ClockCircleOutlined />
              <Space>
                <FormatDate
                  format="ddd, DD MMM YYYY"
                  date={serviceRequest.utc_start_time}
                />
                <span>
                  <FormatDate date={serviceRequest.utc_start_time} onlyTime />
                </span>
              </Space>
            </Space>
          </small>
        </Space>
      </Card>
    );
  return (
    <Card
      className={classNames(styles.appointmentCard, cardClassName, onItemClick && styles.clickable)}
      cardContentClassName={classNames({
        [styles.grayedOutServiceRequest]: isGrayedOut(serviceRequest),
        [styles.noBorder]: !bordered,
        [styles.blockerAppointment]: !serviceRequest.service,
      })}
      onClick={onItemClick ? openDetailsDrawer : undefined}
    >
      <div className={styles.header}>
        <div className={styles.tags}>
          <h4>
            {serviceRequest.title ||
              serviceRequest.branch_service?.service_type?.name}
          </h4>
          {isCustomAppointment(serviceRequest) && (
            <Tag color={serviceRequest.color}>
              <F
                id="pages.serviceRequests.customAppointmentTag"
                defaultMessage="Custom appointment"
              />
            </Tag>
          )}
          {serviceRequest.status && (
            <ServiceRequestStatusTag status={serviceRequest.status} />
          )}
          {serviceRequest?.branch_service?.branch?.offering?.name && (
            <Tag>{serviceRequest?.branch_service?.branch?.offering?.name}</Tag>
          )}
        </div>
        <div className={styles.iconActions} onClick={(e) => onItemClick && e.stopPropagation()}>
          <VideoLinkButton
            videoCall={serviceRequest.video_call}
            size="small"
            shape="circle"
          />
          <ServiceRequestAction
            serviceRequest={serviceRequest}
            lead={lead}
            onActionEnd={onActionEnd}
            ghostActions={ghostActions}
            hideActions={hideActions}
          />
        </div>
      </div>
      {showLead && serviceRequest.lead && (
        <div className={styles.lead} onClick={(e) => onItemClick && e.stopPropagation()}>
          <LeadActionTag lead={serviceRequest.lead} />
        </div>
      )}
      <div className={styles.details}>
        {serviceRequest.description && <p>{serviceRequest.description}</p>}
        <div className={styles.detailsInfo}>
          {serviceRequest.service && (
            <Space size={4} onClick={(e) => onItemClick && e.stopPropagation()}>
              <EnvironmentOutlined />{' '}
              {serviceRequest?.branch_service?.branch?.name ? (
                <BranchActionTag
                  showAddressTooltip
                  size={'small'}
                  branch={serviceRequest?.branch_service?.branch}
                />
              ) : (
                serviceRequest.where
              )}
            </Space>
          )}
          <Space size={4}>
            <ClockCircleOutlined />
            <Space>
              <FormatDate
                format="ddd, DD MMM YYYY"
                date={serviceRequest.utc_start_time}
              />
              <span>
                <FormatDate date={serviceRequest.utc_start_time} onlyTime />
              </span>
            </Space>
          </Space>
          {serviceRequest.created && (
            <Space size={4}>
              <HistoryOutlined />
              <span>
                <F
                  id="pages.leads.serviceRequests.createdOn"
                  defaultMessage="Created {date}"
                  values={{ date: <FormatDate format="D. MMM YYYY" date={serviceRequest.created} /> }}
                />
              </span>
            </Space>
          )}
          {recurringLabel && (
            <Space size={4}>
              <SyncOutlined />
              <span>{recurringLabel}</span>
            </Space>
          )}
          {agent && (
            <Space size={4} onClick={(e) => onItemClick && e.stopPropagation()}>
              <UserOutlined />
              <ServiceRequestAssignee serviceRequest={serviceRequest} />
            </Space>
          )}
          {!hideMigrationSource &&
            serviceRequest?.migration_source &&
            serviceRequest?.migration_id && (
              <Space size={4} onClick={(e) => onItemClick && e.stopPropagation()}>
                <BlockOutlined />
                <ServiceRequestMigrationInfo serviceRequest={serviceRequest} />
              </Space>
            )}
          {!hideExtraFields &&
            serviceRequest.service_request_extra_fields?.length > 0 && (
              <>
                <Button
                  type="link"
                  size="small"
                  icon={
                    <DownOutlined rotate={isExtraFieldsExpanded ? 180 : 0} />
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExtraFieldsExpanded(!isExtraFieldsExpanded);
                  }}
                  className={styles.extraFieldsToggle}
                >
                  <F
                    id="entity.extra_field.plural"
                    defaultMessage="Extra fields"
                  />
                </Button>
                {isExtraFieldsExpanded && (
                  <Card
                    noSpin
                    size="small"
                    className={styles.leadDetailsServiceRequestExtraFieldsCard}
                  >
                    <DynamicExtraFieldsForm
                      labelWidth={120}
                      serviceRequestId={serviceRequest?.id}
                      fields={serviceRequest?.service_request_extra_fields}
                    />
                  </Card>
                )}
              </>
            )}
        </div>
      </div>
    </Card>
  );
}

export default ServiceRequestItem;
